import React, { useCallback, useEffect, useRef, useState } from "react";
import Calendar from "./child/Calendar";
import { Icon } from '@iconify/react';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { toast } from "react-toastify";
import { auth } from "../helper/auth";
import { useAuth } from "../context/AuthContext";
import config from "../config";

// API service for calendar operations
const calendarAPI = {
  // Fetch all events and tasks
  async fetchEvents(userId, filters = {}) {
    const params = new URLSearchParams({ user_id: userId, ...filters });
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events?${params}`);
    return response.json();
  },

  // Create new event/task
  async createEvent(eventData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  // Update event
  async updateEvent(id, eventData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  // Update task
  async updateTask(id, taskData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(taskData),
    });
    return response.json();
  },

  // Delete event
  async deleteEvent(id) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events/${id}`, {
      method: "DELETE",
    });
    return response.json();
  },

  // Get task statistics
  async getTaskStats(userId) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks/stats?user_id=${userId}`);
    return response.json();
  },
};

const DatePicker = ({ id, placeholder }) => {
  const datePickerRef = useRef(null);

  useEffect(() => {
    flatpickr(datePickerRef.current, {
      enableTime: true,
      dateFormat: "d/m/Y H:i",
    });
  }, []);

  return (
    <input
      ref={datePickerRef}
      id={id}
      type="text"
      className="form-control radius-8 bg-base"
      placeholder={placeholder}
    />
  );
};

const CalendarMainLayer = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState("all"); // 'all', 'event', 'task'
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const currentUserId = user ? user.id : null;

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const filters =
        selectedEventType !== "all" ? { event_type: selectedEventType } : {};
      const [eventsResponse, statsResponse] = await Promise.all([
        calendarAPI.fetchEvents(currentUserId, filters),
        calendarAPI.getTaskStats(currentUserId),
      ]);

      if (eventsResponse.success) {
        setEvents(eventsResponse.data);
      }

      if (statsResponse.success) {
        setTaskStats(statsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedEventType, currentUserId]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Helper to safely close modals
  const closeModal = (modalId) => {
    try {
      // 1. Try standard Bootstrap instance method
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
        if (modalInstance) {
          modalInstance.hide();
        } else {
          // 2. Fallback: if instance doesn't exist (e.g. opened via data-attrs),
          // create one just to hide it, or simulate close button click
          const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
          if (closeBtn) {
            closeBtn.click();
          } else {
            // 3. Last resort: remove classes manually (not recommended but functional)
            modalEl.classList.remove("show");
            modalEl.style.display = "none";
            document.body.classList.remove("modal-open");
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) backdrop.remove();
          }
        }
      }
    } catch (error) {
      console.error(`Error closing modal ${modalId}:`, error);
    }
  };

  // Handle event creation
  const handleCreateEvent = async (eventData) => {
    console.log("=== CREATE EVENT DEBUG START ===");
    console.log("1. Event data received:", eventData);
    console.log("2. User ID:", currentUserId);

    try {
      const fullEventData = {
        ...eventData,
        user_id: currentUserId,
      };
      console.log("3. Full event data to send:", fullEventData);

      const response = await calendarAPI.createEvent(fullEventData);
      console.log("4. API response:", response);

      if (response.success) {
        console.log("5. Event created successfully, refreshing data...");
        fetchCalendarData(); // Refresh data

        // Close appropriate modal based on event type
        if (eventData.event_type === "task") {
          closeModal("addTaskModal");
        } else {
          closeModal("exampleModal");
        }

        // Show success message
        toast.success("Created successfully!");

        console.log("=== CREATE EVENT DEBUG END (SUCCESS) ===");
      } else {
        console.error("6. Create failed:", response);
        toast.error(
          "Failed to create event: " + (response.message || "Unknown error")
        );
        console.log("=== CREATE EVENT DEBUG END (FAILURE) ===");
      }
    } catch (error) {
      console.error("7. Error creating event:", error);
      console.log("=== CREATE EVENT DEBUG END (ERROR) ===");
      toast.error("Error creating event: " + error.message);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId) => {
    try {
      // Find the event to determine if it's a task
      const eventToDelete = events.find(
        (e) => e.id.toString() === eventId.toString(),
      );

      let response;
      if (eventToDelete?.event_type === "task") {
        // Delete using task endpoint
        response = await fetch(`/api/calendar/tasks/${eventToDelete.task_id}`, {
          method: "DELETE",
        });
      } else {
        // Delete using event endpoint
        response = await fetch(`/api/calendar/events/${eventId}`, {
          method: "DELETE",
        });
      }

      const result = await response.json();
      if (result.success) {
        fetchCalendarData();
        closeModal("exampleModalDelete");
        toast.success("Deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Error deleting event");
    }
  };

  // Handle event/task update
  const handleUpdateEvent = async (eventData) => {
    try {
      const isTask = selectedEvent?.event_type === "task";
      console.log("=== UPDATE DEBUG START ===");
      console.log("1. Selected event:", selectedEvent);
      console.log("2. Is task:", isTask);
      console.log("3. Event data to send:", eventData);
      console.log("4. Task ID:", selectedEvent?.task_id);
      console.log("5. Event ID:", selectedEvent?.id);

      let response;

      if (isTask) {
        // Update using task endpoint
        const url = `/api/calendar/tasks/${selectedEvent.task_id}`;
        console.log("6. Making PUT request to:", url);
        console.log("7. Request body:", JSON.stringify(eventData, null, 2));

        response = await fetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(eventData),
        });

        console.log("8. Response status:", response.status);
        console.log("9. Response ok:", response.ok);

        const responseData = await response.json();
        console.log("10. Response data:", responseData);
        response = { success: response.ok, ...responseData };
      } else {
        // Update using event endpoint
        response = await calendarAPI.updateEvent(selectedEvent.id, eventData);
        console.log("Event update response:", response);
      }

      console.log("11. Final response:", response);

      if (response.success) {
        console.log("12. Update successful, refreshing data...");
        fetchCalendarData(); // Refresh data

        // Close appropriate modal
        if (isTask) {
          closeModal("editTaskModal");
        } else {
          closeModal("exampleModalEdit");
        }

        toast.success("Updated successfully!");

        console.log("=== UPDATE DEBUG END (SUCCESS) ===");
      } else {
        console.error("13. Update failed:", response);
        toast.error("Failed to update: " + (response.message || "Unknown error"));
        console.log("=== UPDATE DEBUG END (FAILURE) ===");
      }
    } catch (error) {
      console.error("14. Error updating event:", error);
      console.log("=== UPDATE DEBUG END (ERROR) ===");
      toast.error("Error updating: " + error.message);
    }
  };

  return (
    <>
      <div className="row gy-4">
        <div className="col-xxl-3 col-lg-4">
          <div className="card h-100 p-0">
            <div className="card-body p-24">
              <div className="d-flex align-items-center gap-3 mb-32">
                <button
                  type="button"
                  className="btn btn-primary text-sm btn-sm px-12 py-12 flex-grow-1 radius-8 d-flex align-items-center justify-content-center gap-2"
                  data-bs-toggle="modal"
                  data-bs-target="#exampleModal"
                >
                  <Icon
                    icon="fa6-regular:square-plus"
                    className="icon text-lg line-height-1"
                  />
                  Add Event
                </button>
                <button
                  type="button"
                  className="btn btn-success text-sm btn-sm px-12 py-12 flex-grow-1 radius-8 d-flex align-items-center justify-content-center gap-2"
                  data-bs-toggle="modal"
                  data-bs-target="#addTaskModal"
                >
                  <Icon
                    icon="fa6-regular:check-square"
                    className="icon text-lg line-height-1"
                  />
                  Add Task
                </button>
              </div>

              {/* Event Type Filter */}
              <div className="mb-24">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Filter by Type:
                </label>
                <select
                  className="form-select radius-8 bg-base"
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                >
                  <option value="all">All Events & Tasks</option>
                  <option value="event">Events Only</option>
                  <option value="task">Tasks Only</option>
                  <option value="meeting">Meetings</option>
                  <option value="reminder">Reminders</option>
                </select>
              </div>

              {/* Task Stats Summary */}
              {taskStats && (
                <div className="mb-32 p-16 bg-neutral-50 radius-8">
                  <h6 className="text-sm fw-semibold mb-12">Task Overview</h6>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="text-secondary-light text-xs">Pending</span>
                    <span className="text-warning-600 fw-medium text-xs">{taskStats.pending || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="text-secondary-light text-xs">In Progress</span>
                    <span className="text-info-600 fw-medium text-xs">{taskStats.in_progress || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-secondary-light text-xs">Completed</span>
                    <span className="text-success-600 fw-medium text-xs">{taskStats.completed || 0}</span>
                  </div>
                </div>
              )}

              {/* Dynamic Event List */}
              <div className="mt-32">
                {loading ? (
                  <div className="text-center py-24">
                    <div
                      className="spinner-border spinner-border-sm text-primary-600"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : events.length > 0 ? (
                  events.map((event) => (
                    <div
                      key={event.id}
                      className="event-item d-flex align-items-center justify-content-between gap-4 pb-16 mb-16 border border-start-0 border-end-0 border-top-0"
                    >
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-10">
                          <span
                            className={`w-12-px h-12-px rounded-circle fw-medium ${
                              event.event_type === "task"
                                ? "bg-success-600"
                                : event.priority === "high"
                                  ? "bg-danger-600"
                                  : event.priority === "urgent"
                                    ? "bg-danger-800"
                                    : event.priority === "low"
                                      ? "bg-info-600"
                                      : "bg-warning-600"
                            }`}
                          />
                          <span className="text-secondary-light text-xs">
                            {new Date(event.start_date).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                            {event.end_date &&
                              ` - ${new Date(event.end_date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}`}
                          </span>
                          {event.event_type === "task" && (
                            <span className="badge bg-success-100 text-success-600 text-xs px-8 py-2 radius-4">
                              Task
                            </span>
                          )}
                          {event.event_type === "meeting" && (
                            <span className="badge bg-primary-100 text-primary-600 text-xs px-8 py-2 radius-4">
                              Meeting
                            </span>
                          )}
                        </div>
                        <span className="text-primary-light fw-semibold text-sm mt-4 d-block">
                          {event.title}
                        </span>
                        {event.event_type === "task" &&
                          event.progress_percentage !== undefined && (
                            <div className="mt-8">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-secondary-light text-xs">
                                  Progress:
                                </span>
                                <span className="text-primary-light fw-semibold text-xs">
                                  {event.progress_percentage}%
                                </span>
                              </div>
                              <div
                                className="progress"
                                style={{
                                  height: "12px",
                                  borderRadius: "6px",
                                  backgroundColor: "#e5e7eb",
                                }}
                              >
                                <div
                                  className="progress-bar bg-success-600"
                                  style={{
                                    width: `${event.progress_percentage}%`,
                                    borderRadius: "6px",
                                  }}
                                />
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="dropdown">
                        <button
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <Icon
                            icon="entypo:dots-three-vertical"
                            className="icon text-secondary-light"
                          />
                        </button>
                        <ul className="dropdown-menu p-12 border bg-base shadow">
                          <li>
                            <button
                              type="button"
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModalView"
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="hugeicons:view"
                                className="icon text-lg line-height-1"
                              />
                              View
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target={
                                event.event_type === "task"
                                  ? "#editTaskModal"
                                  : "#exampleModalEdit"
                              }
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="lucide:edit"
                                className="icon text-lg line-height-1"
                              />
                              Edit
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="delete-item dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-danger-100 text-hover-danger-600 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModalDelete"
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="icon text-lg line-height-1"
                              />
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-24">
                    <Icon
                      icon="solar:calendar-linear"
                      className="icon text-3xl text-secondary-light mb-8"
                    />
                    <p className="text-secondary-light text-sm">
                      No{" "}
                      {selectedEventType === "all"
                        ? "events or tasks"
                        : selectedEventType + "s"}{" "}
                      found
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-xxl-9 col-lg-8">
          <div className="card h-100 p-0">
            <div className="card-body p-24">
              <div id="wrap">
                <div id="calendar" />
                <div style={{ clear: "both" }} />
                <Calendar
                  events={events}
                  onEventCreate={handleCreateEvent}
                  onEventUpdate={fetchCalendarData}
                  onEventDelete={handleDeleteEvent}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Add Event */}
      <div
        className="modal fade"
        id="exampleModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalLabel">
                Add New Event
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form action="#">
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Event Title :{" "}
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="Enter Event Title "
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="startDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="startDate"
                        placeholder="03/12/2024, 10:30 AM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="endDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="endDate"
                        placeholder="03/12/2024, 2:30 PM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="endDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Label{" "}
                    </label>
                    <div className="d-flex align-items-center flex-wrap gap-28">
                      <div className="form-check checked-success d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Personal"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Personal"
                        >
                          <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                          Personal
                        </label>
                      </div>
                      <div className="form-check checked-primary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Business"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Business"
                        >
                          <span className="w-8-px h-8-px bg-primary-600 rounded-circle" />
                          Business
                        </label>
                      </div>
                      <div className="form-check checked-warning d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Family"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Family"
                        >
                          <span className="w-8-px h-8-px bg-warning-600 rounded-circle" />
                          Family
                        </label>
                      </div>
                      <div className="form-check checked-secondary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Important"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Important"
                        >
                          <span className="w-8-px h-8-px bg-lilac-600 rounded-circle" />
                          Important
                        </label>
                      </div>
                      <div className="form-check checked-danger d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Holiday"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Holiday"
                        >
                          <span className="w-8-px h-8-px bg-danger-600 rounded-circle" />
                          Holiday
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="desc"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="desc"
                      rows={4}
                      cols={50}
                      placeholder="Write some text"
                      defaultValue={""}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                      onClick={(e) => {
                        // Date conversion function
                        const formatDateForDB = (dateString) => {
                          if (!dateString) return null;
                          try {
                            // Convert "25/01/2024 10:30" to "2024-01-25 10:30:00"
                            const [datePart, timePart] = dateString.split(" ");
                            const [day, month, year] = datePart.split("/");
                            const [hours, minutes] = timePart.split(":");

                            // Pad with leading zeros if needed
                            const paddedDay = day.padStart(2, "0");
                            const paddedMonth = month.padStart(2, "0");
                            const paddedHours = hours.padStart(2, "0");
                            const paddedMinutes = minutes.padStart(2, "0");

                            return `${year}-${paddedMonth}-${paddedDay} ${paddedHours}:${paddedMinutes}:00`;
                          } catch (error) {
                            console.error("Date conversion error:", error);
                            return null;
                          }
                        };

                        // Collect form data
                        const form = e.target.closest("form");
                        const formData = {
                          title: form.querySelector(
                            'input[placeholder="Enter Event Title "]',
                          ).value,
                          start_date: formatDateForDB(
                            form.querySelector("#startDate").value,
                          ),
                          end_date: formatDateForDB(
                            form.querySelector("#endDate").value,
                          ),
                          event_type: "event",
                          description: form.querySelector("#desc").value,
                          color: (() => {
                            const selectedLabel = form.querySelector(
                              'input[name="label"]:checked',
                            );
                            if (!selectedLabel) return "#007bff";
                            const labelMap = {
                              Personal: "#10b981",
                              Business: "#007bff",
                              Family: "#f59e0b",
                              Important: "#8b5cf6",
                              Holiday: "#ef4444",
                            };
                            return labelMap[selectedLabel.id] || "#007bff";
                          })(),
                          priority: "medium",
                        };

                        console.log("Saving event data:", formData);
                        console.log("Original dates:", {
                          start: form.querySelector("#startDate").value,
                          end: form.querySelector("#endDate").value,
                        });

                        // Call create handler
                        handleCreateEvent(formData);
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Add Task */}
      <div
        className="modal fade"
        id="addTaskModal"
        tabIndex={-1}
        aria-labelledby="addTaskModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="addTaskModalLabel">
                Add New Task
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const taskData = {
                    title: formData.get("taskTitle"),
                    description: formData.get("taskDescription"),
                    start_date: new Date().toISOString(),
                    end_date: new Date(
                      formData.get("taskDueDate"),
                    ).toISOString(),
                    event_type: "task",
                    priority: formData.get("taskPriority"),
                    task_details: {
                      due_date: new Date(
                        formData.get("taskDueDate"),
                      ).toISOString(),
                      progress_percentage: 0,
                      task_category: formData.get("taskCategory"),
                      estimated_hours:
                        parseFloat(formData.get("estimatedHours")) || null,
                    },
                  };
                  handleCreateEvent(taskData);
                }}
              >
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Task Title :{" "}
                    </label>
                    <input
                      type="text"
                      name="taskTitle"
                      className="form-control radius-8"
                      placeholder="Enter Task Title"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Due Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="taskDueDate"
                        placeholder="03/12/2024, 5:00 PM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Priority
                    </label>
                    <select
                      name="taskPriority"
                      className="form-select radius-8 bg-base"
                      defaultValue="medium"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Category
                    </label>
                    <input
                      type="text"
                      name="taskCategory"
                      className="form-control radius-8"
                      placeholder="e.g., Development, Design"
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      name="estimatedHours"
                      className="form-control radius-8"
                      placeholder="e.g., 4.5"
                      step="0.5"
                      min="0"
                    />
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description
                    </label>
                    <textarea
                      name="taskDescription"
                      className="form-control"
                      rows={4}
                      cols={50}
                      placeholder="Describe the task..."
                      defaultValue={""}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success border border-success-600 text-md px-24 py-12 radius-8"
                    >
                      Create Task
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal View Event */}
      <div
        className="modal fade"
        id="exampleModalView"
        tabIndex={-1}
        aria-labelledby="exampleModalViewLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalViewLabel">
                View Details
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Title
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  Design Conference
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Start Date
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  25 Jan 2024, 10:30AM
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  End Date
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  25 Jan 2024, 2:30AM
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Description
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  N/A
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Label
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4 d-flex align-items-center gap-2">
                  <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                  Business
                </h6>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Edit Event */}
      <div
        className="modal fade"
        id="exampleModalEdit"
        tabIndex={-1}
        aria-labelledby="exampleModalEditLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalEditLabel">
                Edit Event
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form action="#">
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Event Title :{" "}
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="Enter Event Title "
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editstartDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className=" position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="startDate"
                        placeholder="03/12/2024, 10:30 AM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editendDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className=" position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="endDate"
                        placeholder="03/12/2024, 2:30 PM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Label{" "}
                    </label>
                    <div className="d-flex align-items-center flex-wrap gap-28">
                      <div className="form-check checked-success d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editPersonal"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editPersonal"
                        >
                          <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                          Personal
                        </label>
                      </div>
                      <div className="form-check checked-primary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editBusiness"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editBusiness"
                        >
                          <span className="w-8-px h-8-px bg-primary-600 rounded-circle" />
                          Business
                        </label>
                      </div>
                      <div className="form-check checked-warning d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editFamily"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editFamily"
                        >
                          <span className="w-8-px h-8-px bg-warning-600 rounded-circle" />
                          Family
                        </label>
                      </div>
                      <div className="form-check checked-secondary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editImportant"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editImportant"
                        >
                          <span className="w-8-px h-8-px bg-lilac-600 rounded-circle" />
                          Important
                        </label>
                      </div>
                      <div className="form-check checked-danger d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editHoliday"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editHoliday"
                        >
                          <span className="w-8-px h-8-px bg-danger-600 rounded-circle" />
                          Holiday
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="desc"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="editdesc"
                      rows={4}
                      cols={50}
                      placeholder="Write some text"
                      defaultValue={""}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                      onClick={(e) => {
                        // Collect form data
                        const form = e.target.closest("form");
                        const formData = {
                          title: form.querySelector(
                            'input[placeholder="Enter Event Title "]',
                          ).value,
                          start_date: form.querySelector("#startDate").value,
                          end_date: form.querySelector("#endDate").value,
                          event_type: "event",
                          priority: "medium",
                        };

                        console.log("Saving event data:", formData);

                        // Call create handler
                        handleCreateEvent(formData);
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Delete Event */}
      <div
        className="modal fade"
        id="exampleModalDelete"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-sm modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-body p-24 text-center">
              <span className="mb-16 fs-1 line-height-1 text-danger">
                <Icon icon="fluent:delete-24-regular" className="menu-icon" />
              </span>
              <h6 className="text-lg fw-semibold text-primary-light mb-0">
                Are your sure you want to delete this event
              </h6>
              <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                <button
                  type="button"
                  className="w-50 border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-50 btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                  onClick={() => {
                    if (selectedEvent) {
                      handleDeleteEvent(selectedEvent.id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Edit Task Modal */}
      <div
        className="modal fade"
        id="editTaskModal"
        tabIndex={-1}
        aria-labelledby="editTaskModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="editTaskModalLabel">
                Edit Task
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form action="#">
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Task Title :
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      placeholder="Enter Task Title"
                      defaultValue={selectedEvent?.title || ""}
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editTaskStartDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editTaskStartDate"
                        placeholder="03/12/2024, 10:30 AM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editTaskEndDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editTaskEndDate"
                        placeholder="03/12/2024, 11:30 AM"
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Priority
                    </label>
                    <select
                      className="form-select radius-8 bg-base"
                      defaultValue={selectedEvent?.priority || "medium"}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Progress (%)
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      placeholder="0"
                      min="0"
                      max="100"
                      defaultValue={selectedEvent?.progress_percentage || 0}
                    />
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description
                    </label>
                    <textarea
                      className="form-control radius-8"
                      rows={3}
                      placeholder="Enter task description"
                      defaultValue={selectedEvent?.description || ""}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <button
                    type="reset"
                    className="border border-neutral-300 bg-hover-neutral-200 text-neutral-600 text-md px-40 py-11 radius-8"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-24 py-12 radius-8"
                    onClick={(e) => {
                      // Collect form data with better selectors
                      const form = e.target.closest("form");
                      const formData = {
                        title: form.querySelector(
                          'input[placeholder="Enter Task Title"]',
                        ).value,
                        start_date:
                          form.querySelector("#editTaskStartDate").value,
                        end_date: form.querySelector("#editTaskEndDate").value,
                        priority: form.querySelector("select").value,
                        progress_percentage:
                          parseInt(
                            form.querySelector('input[type="number"]').value,
                          ) || 0,
                        description: form.querySelector("textarea").value,
                      };

                      console.log("Saving task data:", formData);
                      console.log("Selected event:", selectedEvent);

                      // Call update handler
                      handleUpdateEvent(formData);
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarMainLayer;
